"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { Check, Minus } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { authFetch } from "../../lib/authFetch";
import { API_BASE } from "../../lib/api";
import { PLANS, STUDY_PASSES, ctaLabelForPlan, type CheckoutTarget } from "../../lib/pricingPlans";
import Faq from "../components/Faq";
import { FAQ_ALL } from "../../lib/faqData";
import { track, setEntitlementContext } from "../../lib/analytics";

// Feature comparison rows. Numbers mirror backend/rate_limits.py exactly.
// Two columns since the single-level repackaging: Free vs Full access
// (a study pass or the monthly plan grant the same feature set -- the
// "max" tier internally). Cell values:
//   true        -> green check
//   false       -> grey dash (not included)
//   any string  -> rendered verbatim (use for quota labels like "20 / day")
type Cell = true | false | string;
type CompareRow = { feature: string; free: Cell; paid: Cell };
type CompareGroup = { heading: string; rows: CompareRow[] };

const COMPARISON: CompareGroup[] = [
  {
    heading: "Record your mistakes",
    rows: [
      { feature: "Logged questions", free: "Unlimited", paid: "Unlimited" },
      { feature: "Your own reflections and error tags", free: true, paid: true },
      { feature: "Full question history (view, edit, delete)", free: true, paid: true },
      { feature: "Full-length score logging", free: true, paid: true },
      { feature: "Data export — yours forever", free: true, paid: true },
    ],
  },
  {
    heading: "Understand and improve",
    rows: [
      { feature: "Mistake Insights: recurring patterns, ranked by cost", free: false, paid: true },
      { feature: "Priority weaknesses with why they rank", free: false, paid: true },
      { feature: "Improvement tracking per weakness", free: false, paid: true },
      { feature: "Recommended next actions after every analysis", free: false, paid: true },
      { feature: "Weak-area dashboard and error-tag trends", free: false, paid: true },
      { feature: "Auto-generated weekly study plan", free: false, paid: true },
      { feature: "Anki integration: deck health and card push", free: false, paid: true },
    ],
  },
  {
    heading: "Maren AI coach",
    rows: [
      // "Unlimited*" cells share the fair-use footnote at the bottom of
      // the table. Real per-day ceilings live in backend/rate_limits.py
      // (250 chats, 100 quizzes, etc.) -- set so genuine heavy use
      // cannot reach them.
      { feature: "Maren AI chat with full context", free: false, paid: "Unlimited*" },
      { feature: "Worked-solution explainer", free: false, paid: "Unlimited*" },
      { feature: "Practice quiz generator", free: false, paid: "Unlimited*" },
      { feature: "AI flashcard generation", free: false, paid: "Unlimited*" },
      { feature: "Photo-of-a-question scanning", free: false, paid: "Unlimited*" },
      { feature: "Auto-classify new questions", free: false, paid: true },
      { feature: "Daily personalized focus", free: false, paid: true },
    ],
  },
  {
    heading: "Smart Calendar",
    rows: [
      { feature: "Smart Replan (Maren rebuilds your week)", free: false, paid: "Unlimited" },
      { feature: "Auto-reschedule when you fall behind", free: false, paid: true },
    ],
  },
  {
    heading: "Support",
    rows: [
      { feature: "Email support", free: true, paid: true },
      { feature: "Priority response", free: false, paid: true },
      { feature: "Priority on new feature releases", free: false, paid: true },
    ],
  },
];

// FAQ data moved to lib/faqData.ts so the same canonical answers power
// the homepage and /help. The pricing page renders the FULL set
// (FAQ_ALL = trust + plan-mechanics) via the shared Faq component below.

export default function PricingPage() {
  const { session } = useAuth();
  const router = useRouter();
  const [loading, setLoading] = useState<string | null>(null);
  const [currentPlan, setCurrentPlan] = useState<string | null>(null);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [cancelNotice, setCancelNotice] = useState(false);

  // Pricing intent -- top of the upgrade funnel.
  useEffect(() => {
    track({ name: "pricing_page_view" });
  }, []);

  useEffect(() => {
    if (!session) return;
    authFetch(`${API_BASE}/billing/plan`)
      .then((r) => r.json())
      .then((d) => {
        setCurrentPlan(d.plan);
        setEntitlementContext(d.plan ?? "free", d.capabilities?.grandfathered);
      })
      .catch(() => {});
  }, [session]);

  // Returning from a cancelled Stripe Checkout (cancel_url carries
  // ?checkout=cancelled). Read from window rather than useSearchParams so
  // this page doesn't need a Suspense boundary.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (new URLSearchParams(window.location.search).get("checkout") === "cancelled") {
      setCancelNotice(true);
    }
  }, []);

  // Handles both the monthly tiers and the one-time study passes: any
  // purchasable checkout target flows through the same endpoint.
  async function handleUpgrade(target: CheckoutTarget) {
    // Observability hook -- founder reported CTAs "doing nothing"; this
    // makes the click path inspectable in DevTools without flipping a
    // build flag. Cheap, no PII, safe to keep in production.
    console.log("pricing_cta_click", { target, hasSession: !!session });

    // Split pricing intent by tier so free-vs-paid interest is a direct
    // funnel dimension rather than a location-string parse.
    if (target === "free") {
      track({ name: "free_plan_cta_click", props: { location: "pricing_page" } });
    } else {
      track({ name: "paid_plan_cta_click", props: { location: "pricing_page", plan: target } });
    }

    if (target === "free") {
      if (!session) {
        router.push("/signup");
      } else {
        // Already authenticated visitors clicking "Start Free" used to
        // silently no-op. Route them to the dashboard so the click does
        // something visible; the query param lets the dashboard
        // optionally surface a toast acknowledging the navigation.
        router.push("/dashboard?from=pricing_free");
      }
      return;
    }
    // Unauthed visitors hitting a paid CTA go to signup (where they can
    // actually create an account) rather than login (which dead-ends for
    // first-time visitors with no credentials yet).
    if (!session) {
      router.push(`/signup?plan=${target}`);
      return;
    }
    setLoading(target);
    setCheckoutError(null);
    try {
      const res = await authFetch(`${API_BASE}/billing/create-checkout-session`, {
        method: "POST",
        body: JSON.stringify({ plan: target }),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        setCheckoutError(errData.detail || `Checkout failed (${res.status}).`);
        return;
      }
      const data = await res.json();
      if (data.url) {
        // target is narrowed to a paid tier or a pass here: the free and
        // unauthed paths returned above.
        track({
          name: "checkout_started",
          props: { plan: target as "pro" | "max" | "pass_3mo" | "pass_6mo" },
        });
        window.location.href = data.url;
      } else {
        setCheckoutError("Checkout couldn't be started. Try again, or contact support.");
      }
    } catch (e) {
      console.error("pricing_checkout_error", e);
      setCheckoutError("Something went wrong. Try again in a moment.");
    } finally {
      setLoading(null);
    }
  }

  return (
    <main className="flex flex-col items-center pt-10 pb-20 px-4">
      <div className="w-full max-w-6xl">
        <div className="mb-10 text-center">
          <h1 className="text-2xl font-display font-medium text-maren-charcoal dark:text-maren-stone">Choose your plan</h1>
          <p className="text-sm text-maren-charcoal/60 dark:text-maren-stone/60 mt-2">
            Start free, no card. No hidden fees.
          </p>
        </div>

        {cancelNotice && (
          <div
            role="status"
            className="mb-6 flex items-start justify-between gap-4 rounded-md border border-maren-sage/40 dark:border-maren-sage/20 bg-maren-stone/40 dark:bg-maren-charcoal-elevated/30 px-4 py-3"
          >
            <p className="text-sm text-maren-charcoal/75 dark:text-maren-stone/75 leading-snug">
              Checkout cancelled — no charge was made. Pick a plan whenever you&apos;re ready.
            </p>
            <button
              type="button"
              onClick={() => setCancelNotice(false)}
              className="shrink-0 text-xs font-semibold text-maren-green dark:text-maren-gold hover:underline"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* One buying decision: track free, or buy the window you are
            studying. Passes grant the full feature set, paid once. The
            single monthly option lives in a deliberately quiet row below
            the grid. DRAFT copy pending the wording review. */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {/* Free Tracker card */}
          <div className="relative rounded-2xl border border-maren-sage/40 dark:border-maren-sage/20 bg-white dark:bg-maren-charcoal shadow-sm p-6 flex flex-col">
            {currentPlan === "free" && (
              <span className="absolute top-4 right-4 rounded-full bg-emerald-100 dark:bg-emerald-900/40 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-emerald-700 dark:text-emerald-400">
                Current
              </span>
            )}
            <h3 className="text-lg font-bold text-maren-charcoal dark:text-maren-stone">Free Tracker</h3>
            <div className="mt-2 mb-1">
              <span className="text-3xl font-extrabold text-maren-charcoal dark:text-maren-stone">$0</span>
              <span className="text-sm text-maren-charcoal/60 dark:text-maren-stone/60"> forever</span>
            </div>
            <p className="text-sm text-maren-charcoal/60 dark:text-maren-stone/60 mb-5 leading-snug">
              Log it. Explain it. Keep it organized. Your mistake record, free forever.
            </p>
            <ul className="space-y-2 mb-7 flex-1">
              {PLANS[0].features.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm text-maren-charcoal/80 dark:text-maren-stone/80">
                  <Check className="w-4 h-4 text-maren-green dark:text-maren-gold shrink-0 mt-0.5" strokeWidth={2.5} />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            {!session ? (
              <Link
                href="/signup"
                className="block w-full text-center rounded-md border border-maren-green dark:border-maren-gold px-4 py-2.5 text-sm font-bold text-maren-green dark:text-maren-gold hover:bg-maren-gold/10 dark:hover:bg-maren-gold/15 transition"
              >
                Start free
              </Link>
            ) : (
              <button
                type="button"
                // Inert for every signed-in visitor. A free user already
                // has this plan; a paid user already has everything in it.
                // Previously only free users were disabled, so PAYING
                // users got a live button labelled "Your current plan"
                // that fired free_plan_cta_click and corrupted the
                // free-vs-paid intent split it exists to measure.
                disabled
                onClick={() => handleUpgrade("free")}
                className="w-full rounded-md border border-maren-green dark:border-maren-gold px-4 py-2.5 text-sm font-bold text-maren-green dark:text-maren-gold hover:bg-maren-gold/10 dark:hover:bg-maren-gold/15 transition disabled:opacity-50"
              >
                {ctaLabelForPlan("free", true, currentPlan)}
              </button>
            )}
          </div>

          {STUDY_PASSES.map((pass) => (
            <div
              key={pass.id}
              className={`relative rounded-2xl border p-6 flex flex-col ${
                pass.featured
                  ? "border-maren-gold shadow-lg shadow-maren-gold/15 dark:shadow-maren-gold/20 bg-white dark:bg-maren-charcoal"
                  : "border-maren-sage/40 dark:border-maren-sage/20 bg-white dark:bg-maren-charcoal shadow-sm"
              }`}
            >
              {pass.featured && (
                <span className="absolute top-4 right-4 rounded-full bg-maren-green px-2 py-0.5 text-[10px] font-bold text-white uppercase tracking-wide">
                  Best value
                </span>
              )}
              <h3 className="text-lg font-bold text-maren-charcoal dark:text-maren-stone">{pass.name}</h3>
              <div className="mt-2 mb-1 flex items-baseline gap-2">
                <span className="text-3xl font-extrabold text-maren-charcoal dark:text-maren-stone">{pass.price}</span>
                <span className="text-sm text-maren-charcoal/60 dark:text-maren-stone/60">
                  one time · {pass.perMonth}
                </span>
              </div>
              <p className="text-sm text-maren-charcoal/60 dark:text-maren-stone/60 mb-5 leading-snug">{pass.description}</p>
              <ul className="space-y-2 mb-7 flex-1">
                {[
                  `${pass.months} months of full access`,
                  "Every Maren AI feature: chat, quizzes, explanations, flashcards",
                  "Unlimited question logging and smart replanning",
                  "Paid once, no monthly rebill",
                ].map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-maren-charcoal/80 dark:text-maren-stone/80">
                    <Check className="w-4 h-4 text-maren-green dark:text-maren-gold shrink-0 mt-0.5" strokeWidth={2.5} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              {!session ? (
                <Link
                  href={`/signup?plan=${pass.id}`}
                  className={`block w-full text-center rounded-md px-4 py-2.5 text-sm font-bold transition ${
                    pass.featured
                      ? "bg-maren-green text-maren-stone hover:bg-maren-green/90"
                      : "border border-maren-green dark:border-maren-gold text-maren-green dark:text-maren-gold hover:bg-maren-gold/10 dark:hover:bg-maren-gold/15"
                  }`}
                >
                  Get started
                </Link>
              ) : (
                <button
                  type="button"
                  disabled={loading === pass.id}
                  onClick={() => handleUpgrade(pass.id)}
                  className={`w-full rounded-md px-4 py-2.5 text-sm font-bold transition ${
                    pass.featured
                      ? "bg-maren-green text-maren-stone hover:bg-maren-green/90 disabled:opacity-50"
                      : "border border-maren-green dark:border-maren-gold text-maren-green dark:text-maren-gold hover:bg-maren-gold/10 dark:hover:bg-maren-gold/15 disabled:opacity-50"
                  }`}
                >
                  {loading === pass.id ? "Redirecting…" : "Get started"}
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Monthly: one level, deliberately the quiet, worse-value option
            (the passes are the deal). Single muted row, not a card. Kept
            id="max" so existing /pricing#max deep links (TellMaren CTAs)
            still land on the monthly full-access option; internally the
            monthly plan is still the "max" tier, so checkout is unchanged
            and existing subscribers stay grandfathered. */}
        <div
          id="max"
          className="mt-8 max-w-3xl mx-auto rounded-xl border border-maren-sage/40 dark:border-maren-sage/20 bg-maren-stone/40 dark:bg-maren-charcoal-elevated/30 px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5"
        >
          <p className="flex-1 text-sm text-maren-charcoal/70 dark:text-maren-stone/70 leading-relaxed">
            <span className="font-semibold text-maren-charcoal dark:text-maren-stone">
              Prefer month to month?
            </span>{" "}
            Monthly access is $39 a month for the same full feature set,
            cancel anytime. For a full prep, the study passes cost less.
          </p>
          {currentPlan === "pro" || currentPlan === "max" ? (
            <button
              type="button"
              disabled={loading === "max"}
              onClick={() => handleUpgrade("max")}
              className="shrink-0 text-sm font-semibold text-maren-green dark:text-maren-gold hover:underline underline-offset-4 disabled:opacity-50"
            >
              Manage subscription
            </button>
          ) : !session ? (
            <Link
              href="/signup?plan=max"
              className="shrink-0 text-sm font-semibold text-maren-green dark:text-maren-gold hover:underline underline-offset-4"
            >
              Get monthly access
            </Link>
          ) : (
            <button
              type="button"
              disabled={loading === "max"}
              onClick={() => handleUpgrade("max")}
              className="shrink-0 text-sm font-semibold text-maren-green dark:text-maren-gold hover:underline underline-offset-4 disabled:opacity-50"
            >
              {loading === "max" ? "Redirecting…" : "Get monthly access"}
            </button>
          )}
        </div>

        {checkoutError && (
          // Inline error row -- replaces the previous silent failure +
          // alert() path. Renders directly under the three pricing cards
          // so the user sees the cause near the button they just clicked.
          // Terracotta tone matches the existing Maren error styling
          // (see WaitlistInset, TellMaren validity messages).
          <div
            role="alert"
            className="mt-6 flex items-start justify-between gap-4 rounded-md border border-terracotta/40 bg-terracotta/5 dark:bg-terracotta/10 px-4 py-3"
          >
            <p className="text-sm text-terracotta dark:text-terracotta leading-snug">
              {checkoutError}
            </p>
            <button
              type="button"
              onClick={() => setCheckoutError(null)}
              className="shrink-0 text-xs font-semibold text-terracotta hover:underline"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Trust + AI accuracy reassurance per Hermes audit fix I8.
            Sits between the tier cards and the comparison table so
            it's visible at the moment of decision, not buried in legal. */}
        <p className="mt-6 text-center text-xs text-maren-charcoal/55 dark:text-maren-stone/55 max-w-2xl mx-auto leading-relaxed">
          Your study data is private. We do not sell it. AI outputs may contain errors; verify
          against official materials. Maren is a study tool, not a guarantee of exam performance.
        </p>

        {/* Comparison table -- gives visitors a side-by-side view of
            exactly what each tier includes and at what quota. Anchors
            the upgrade decision and removes the "what am I actually
            paying for?" friction the founder flagged on May 13. */}
        <section className="mt-16 max-w-5xl mx-auto">
          <h3 className="text-lg font-bold text-maren-charcoal dark:text-maren-stone mb-1 text-center">
            Every feature, side by side.
          </h3>
          <p className="text-sm text-maren-charcoal/60 dark:text-maren-stone/60 mb-6 text-center">
            Full access is the same feature set whether you buy a pass or go monthly.
          </p>
          <div className="overflow-x-auto rounded-2xl border border-maren-sage/40 dark:border-maren-sage/20 bg-white dark:bg-maren-charcoal">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-maren-sage/40 dark:border-maren-sage/20 bg-maren-stone/50 dark:bg-maren-charcoal-elevated/40">
                  <th className="text-left font-semibold text-maren-charcoal dark:text-maren-stone px-5 py-3.5 w-[46%]">
                    Feature
                  </th>
                  <th className="text-center font-semibold text-maren-charcoal/70 dark:text-maren-stone/70 px-3 py-3.5">
                    Free
                  </th>
                  <th className="text-center font-semibold text-maren-green dark:text-maren-gold px-3 py-3.5 bg-maren-gold/10 dark:bg-maren-gold/15">
                    Full access
                  </th>
                </tr>
              </thead>
              <tbody>
                {COMPARISON.map((group) => (
                  <>
                    <tr key={`group-${group.heading}`} className="border-t border-maren-sage/40 dark:border-maren-sage/20">
                      <td
                        colSpan={3}
                        className="px-5 py-2.5 text-[11px] font-bold uppercase tracking-[0.18em] text-maren-charcoal/55 dark:text-maren-stone/55 bg-maren-stone/30 dark:bg-maren-charcoal-elevated/20"
                      >
                        {group.heading}
                      </td>
                    </tr>
                    {group.rows.map((row) => (
                      <tr
                        key={`${group.heading}-${row.feature}`}
                        className="border-t border-maren-sage/30 dark:border-maren-sage/15 hover:bg-maren-stone/30 dark:hover:bg-maren-charcoal-elevated/20 transition"
                      >
                        <td className="px-5 py-3 text-maren-charcoal/85 dark:text-maren-stone/85">
                          {row.feature}
                        </td>
                        <CompareCell value={row.free} />
                        <CompareCell value={row.paid} highlight />
                      </tr>
                    ))}
                  </>
                ))}
              </tbody>
            </table>
          </div>
          {/* Fair-use disclosure for the "Unlimited*" cells.
              The cap exists to stop scripted abuse, not to meter real
              students. The ceiling is set ~10x above what a genuine
              heavy premed could reach in a day; if you hit it, you
              were doing something the product was not designed for. */}
          <p className="mt-3 text-center text-[11px] text-maren-charcoal/55 dark:text-maren-stone/55 max-w-2xl mx-auto leading-relaxed">
            *Unlimited use is subject to fair use. Daily ceilings are set well above what any
            student studying for the MCAT could reach. They exist to prevent automated abuse, not
            to meter your prep.
          </p>
        </section>

      </div>
      {/* FAQ. Lives outside the max-w-6xl content column so its own
          max-w-3xl reads-better wrapper can center independently. The
          trust questions (cheating, AAMC, training data) sit alongside
          the plan-mechanics questions because at the buy moment a pre-med
          is weighing both kinds of objection at once. */}
      <Faq
        items={FAQ_ALL}
        heading="Questions we get asked"
        eyebrow="FAQ"
        id="faq"
      />
    </main>
  );
}

/**
 * Single cell in the comparison table. Four shapes:
 *   true                 -> green check
 *   false                -> grey dash (the feature is not in this tier)
 *   "Coming ..."         -> dashed-border gold pill (roadmap commitment)
 *   any other string     -> rendered verbatim (use for quotas like "20 a day")
 *
 * Pro column gets a subtle gold tint via `highlight` so the eye lands
 * on the recommended tier when scanning the table.
 */
function CompareCell({ value, highlight }: { value: Cell; highlight?: boolean }) {
  const bg = highlight ? "bg-maren-gold/8 dark:bg-maren-gold/12" : "";
  if (value === true) {
    return (
      <td className={`text-center px-3 py-3 ${bg}`}>
        <Check
          className="inline-block w-4 h-4 text-maren-green dark:text-maren-gold"
          strokeWidth={2.5}
          aria-label="Included"
        />
      </td>
    );
  }
  if (value === false) {
    return (
      <td className={`text-center px-3 py-3 ${bg}`}>
        <Minus
          className="inline-block w-4 h-4 text-maren-charcoal/30 dark:text-maren-stone/30"
          strokeWidth={2}
          aria-label="Not included"
        />
      </td>
    );
  }
  // Roadmap commitments render with a dashed gold pill so the visual
  // weight reads as "real plan, not yet live" -- distinct from both
  // a shipped feature (solid check) and a quota number (plain text).
  if (typeof value === "string" && value.startsWith("Coming")) {
    return (
      <td className={`text-center px-3 py-3 ${bg}`}>
        <span className="inline-flex items-center rounded-full border border-dashed border-maren-gold/60 dark:border-maren-gold/50 bg-maren-gold/10 dark:bg-maren-gold/15 px-2.5 py-0.5 text-[11px] font-semibold text-maren-green dark:text-maren-gold">
          {value}
        </span>
      </td>
    );
  }
  return (
    <td className={`text-center px-3 py-3 text-xs sm:text-sm text-maren-charcoal/85 dark:text-maren-stone/85 ${bg}`}>
      {value}
    </td>
  );
}
